import React, { HTMLAttributes, useEffect, useMemo, useState } from "react"
import { makeStyles } from "@material-ui/core"
import { colors } from "../../utils/theme"

type ToggleOptions = [string, string]

export type ToogleProps = {
  options: ToggleOptions
  defaultValue?: string
  value?: string
  onChange?: (value: string) => void
  className?: string
  /**
   * Visual style variant. Default keeps the pill-style toggle; "icon"
   * renders two circular icon buttons like a segmented control.
   */
  variant?: "default" | "icon"
  renderLabel?: (value: string) => React.ReactNode
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange">

const Toggle = ({
  options,
  defaultValue,
  value,
  onChange,
  className,
  variant = "default",
  renderLabel,
  ...rest
}: ToogleProps) => {
  const classes = useStyles()
  const isControlled = value !== undefined

  const resolvedDefault = useMemo<string>(() => {
    if (isControlled && value !== undefined) {
      return value
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    return options[0] ?? ""
  }, [defaultValue, isControlled, options, value])

  const [selected, setSelected] = useState<string>(resolvedDefault)

  useEffect(() => {
    if (isControlled && value !== undefined) {
      setSelected(value)
    }
  }, [isControlled, value])

  useEffect(() => {
    if (!isControlled) {
      setSelected(resolvedDefault)
    }
  }, [resolvedDefault, isControlled])

  const handleSelect = (
    nextValue: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation()
    if (!isControlled) {
      setSelected(nextValue)
    }
    onChange?.(nextValue)
  }

  const indicatorIndex = useMemo(() => {
    const index = options.findIndex((option) => option === selected)
    return index < 0 ? 0 : index
  }, [options, selected])

  return (
    <div
      id="toggle"
      className={`${classes.root} ${
        variant === "icon" ? classes.rootIcon : ""
      } ${className ?? ""}`}
      role="group"
      {...rest}
    >
      <div
        className={`${classes.indicator} ${
          variant === "icon" ? classes.indicatorHidden : ""
        }`}
        style={{ transform: `translateX(${indicatorIndex * 100}%)` }}
        aria-hidden
      />
      {options.map((option) => {
        const isSelected = option === selected
        return (
          <button
            key={option}
            type="button"
            className={`${classes.button} ${
              variant === "icon" ? classes.buttonIcon : ""
            }${isSelected ? ` ${classes.selected}` : ""}${
              variant === "icon" && isSelected
                ? ` ${classes.buttonIconSelected}`
                : ""
            }`}
            onClick={(event) => handleSelect(option, event)}
            aria-pressed={isSelected}
          >
            {renderLabel ? renderLabel(option) : option}
          </button>
        )
      })}
    </div>
  )
}

export default Toggle

const useStyles = makeStyles({
  root: {
    display: "inline-flex",
    alignItems: "stretch",
    position: "relative",
    padding: 4,
    borderRadius: 999,
    backgroundColor: "#EEEFF2",
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "inset 0px 1px 2px rgba(30, 38, 60, 0.15)",
    overflow: "hidden",
  },
  rootIcon: {
    padding: 0,
    borderRadius: 0,
    border: "none",
    backgroundColor: "transparent",
    boxShadow: "none",
    gap: 12,
  },
  indicator: {
    position: "absolute",
    top: 4,
    bottom: 4,
    left: 4,
    width: "calc((100% - 8px) / 2)",
    borderRadius: 999,
    backgroundColor: colors.neutral.white,
    boxShadow: "0px 4px 12px rgba(30, 38, 60, 0.15)",
    transition: "transform 0.25s ease",
  },
  indicatorHidden: {
    display: "none",
  },
  button: {
    border: "none",
    background: "transparent",
    borderRadius: 999,
    padding: "8px 28px",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "inherit",
    color: colors.neutral.space,
    cursor: "pointer",
    transition: "color 0.2s ease, font-weight 0.2s ease",
    flex: 1,
    minWidth: 0,
    zIndex: 1,
    whiteSpace: "nowrap",
    justifyContent: "center",
    display: "flex",
    alignItems: "center",
  },
  buttonIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    padding: 0,
    backgroundColor: "#F4F5F9",
  },
  selected: {
    color: colors.neutral.space,
    fontWeight: 700,
  },
  buttonIconSelected: {
    backgroundColor: "#545E78",
    color: colors.neutral.white,
  },
})
